import { useCallback, useEffect, useState } from 'react';
import { Package, Plus, Edit2, Search, X, Trash2 } from 'lucide-react';
import { Button } from '../../../ui/button';
import { Input } from '../../../ui/input';
import {
  Card,
  CardContent,
  // CardHeader, 
  // CardTitle,
  //  CardDescription 
} from '../../../ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../ui/table';
import { Badge } from '../../../ui/badge';
import { Switch } from '../../../ui/switch';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { LawazimConfig, UpdateLawazimStatusPayload } from '../../../types/lawazimConfig';
import { useScreenPermission } from "hooks/useScreenPermission";
import { deleteLawazimConfig, getLawazimList, UpdateLawazimStatus } from 'services/lawazimConfigService';
import { FullScreenLoading } from 'components/ui/loader';
import ConfirmDialog from 'ui/ConfirmDialog';
import Pagination from 'ui/pagination';
import { usePageHeader } from 'contexts/PageHeaderContext';



export function LawazimConfiguration() {
  const navigate = useNavigate()
  const { setPageHeader } = usePageHeader();
  const { canAdd, canEdit, canDelete, screenName } = useScreenPermission();
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [lawazimConfigs, setLawazimConfigs] = useState<LawazimConfig[]>([]);
  const [LawazimTotalCount, setLawazimTotalCount] = useState(0);
  const [selectedItem, setSelectedItem] = useState<LawazimConfig | null>(null);
  const [isStatusDialogOpen, setIsStatusDialogOpen] = useState(false);
  const [LawazimDeleteId, setLawazimDeleteId] = useState<string | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const [filters, setFilters] = useState({
    status: '',
    pageNo: 1,
    pageSize: 20,
  });

  useEffect(() => {
    setPageHeader({
      title: screenName,
      icon: <Package className="h-6 w-6" />,
    });

    return () => setPageHeader(null);
  }, [screenName, setPageHeader]);


  // const filteredLawazimConfigs = lawazimConfigs.filter((config) => {
  //   if (!searchTerm) return true;

  //   const searchLower = searchTerm.toLowerCase();
  //   const normalizedChargeType = String(config.chargeType ?? '').toLowerCase();
  //   const chargeTypeText = normalizedChargeType === '1' || normalizedChargeType === 'daily-rate' || normalizedChargeType === 'dailyrate'
  //     ? 'daily rate'
  //     : normalizedChargeType === '2' || normalizedChargeType === 'fixed-rate' || normalizedChargeType === 'fixedrate'
  //       ? 'fixed rate'
  //       : 'unknown';

  //   return (
  //     (config?.code ?? "").toLowerCase().includes(searchLower) ||
  //     (config?.name ?? "").toLowerCase().includes(searchLower) ||
  //     chargeTypeText.includes(searchLower)
  //   );
  // });


  // API CALLS START
  const fetchLawazimList = useCallback(async () => {
    try {
      setLoading(true);
      const response = await getLawazimList({
        pageNumber: filters?.pageNo ?? 1,
        pageSize: filters?.pageSize ?? 500,
        keyword: searchTerm || '',
      });
      if (response.success) {
        setLawazimConfigs(response.data);
        setLawazimTotalCount(response.totalRecords); // or totalCount if your API returns that
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [filters.pageNo, filters.pageSize, searchTerm]);

  useEffect(() => {
    fetchLawazimList();
  }, [fetchLawazimList]);
  // API CALLS END


  //STATUS CHANGE FUNCTIONALITY START
  const toggleStatus = (item: LawazimConfig) => {
    setSelectedItem(item);
    setIsStatusDialogOpen(true);
  };

  const cancelStatusChange = () => {
    setSelectedItem(null);
    setIsStatusDialogOpen(false);
  };


  const confirmStatusChange = async () => {
    if (!selectedItem) return;
    try {
      const payload: UpdateLawazimStatusPayload = {
        publicId: String(selectedItem.publicId),
        isActive: !selectedItem.isActive,
      };
      await UpdateLawazimStatus(payload);
      //UPDATE LOCAL STATE INSTEAD OF CALLING API AGAIN
      setLawazimConfigs((prev) =>
        prev.map((item) => item.publicId === selectedItem.publicId
          ? { ...item, isActive: !item.isActive } : item));
      toast.success(
        `Lawazim ${selectedItem.isActive ? "deactivated" : "activated"} successfully`
      );
    } catch (error) {
      console.error(error);
      toast.error("Failed to update status");
    } finally {
      setIsStatusDialogOpen(false);
      setSelectedItem(null);
    }
  };
  // STATUS CHANGE FUNCTIONALITY END


  // DELETE ZIYARAT FUNCTIONLITY START
  const handleDeleteAtraaf = (id: string) => {
    setLawazimDeleteId(id);
    setDeleteDialogOpen(true);
  };

  const cancelDeleteDraft = () => {
    setDeleteDialogOpen(false);
    setLawazimDeleteId(null);
  };

  const confirmDeleteLawazim = async () => {
    if (!LawazimDeleteId) return;
    try {
      setLoading(true);
      await deleteLawazimConfig(LawazimDeleteId);
      toast.success("Lawazim deleted successfully");
      await fetchLawazimList();
      setDeleteDialogOpen(false);
      setLawazimDeleteId(null);
    } catch (error) {
      toast.error("Failed to delete");
    } finally {
      setLoading(false);
    }
  };
  // DELETE ZIYARAT FUNCTIONLITY END


  return (
    <div className="">
      <div className="mx-auto">
        {loading && <FullScreenLoading />}
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
          <div className="relative w-full">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <Input
              type="text"
              placeholder="Search by code, lawazim name..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setFilters(prev => ({ ...prev, pageNo: 1 }))
              }}
              className="w-full pl-10 pr-10 h-10 bg-white border-gray-300"
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>

          <div className="flex gap-2">
            {canAdd && (
              <Button
                onClick={() => { navigate('/lawazim-master/add') }}
                style={{ background: '#5B9BD5' }}
                className="text-white"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Lawazim
              </Button>
            )}
          </div>

        </div>

        {/* Search Bar */}
        {/* <Card className="border-[#5B9BD5]/30 mb-4">
          <CardContent className="p-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <Input
                type="text"
                placeholder="Search by code, lawazim name..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setFilters(prev => ({ ...prev, pageNo: 1 }))
                }}
                className="pl-10 pr-10 h-10 bg-white border-gray-300"
              />
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm('')}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
          </CardContent>
        </Card> */}

        {/* Table */}
        <Card className="bg-white border-[#5B9BD5]/30">
          {/* <CardHeader className="p-4 border-b bg-gradient-to-r from-sky-50 to-blue-50 rounded-t-xl">
            <CardTitle style={{ color: '#5B9BD5' }}>Lawazim Configurations</CardTitle>
            <CardDescription className='mb-4'>
              {filteredLawazimConfigs.length} configuration{filteredLawazimConfigs.length !== 1 ? 's' : ''} found
              {searchTerm && ` (filtered from ${lawazimConfigs.length} total)`}
            </CardDescription>
          </CardHeader> */}
          <CardContent className="p-0">
            <div className="p-0 overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow style={{ backgroundColor: '#E3F2FD' }}>
                    <TableHead style={{ color: '#5B9BD5' }} className="px-4 py-3 text-xs">Code</TableHead>
                    <TableHead style={{ color: '#5B9BD5' }} className="px-4 py-3 text-xs">Lawazim Name</TableHead>
                    <TableHead style={{ color: '#5B9BD5' }} className="px-4 py-3 text-xs">Charge Type</TableHead>
                    <TableHead style={{ color: '#5B9BD5' }} className="px-4 py-3 text-xs">Valid From</TableHead>
                    <TableHead style={{ color: '#5B9BD5' }} className="px-4 py-3 text-xs">Valid To</TableHead>
                    <TableHead style={{ color: '#5B9BD5' }} className="px-4 py-3 text-xs text-center">Status</TableHead>
                    {(canEdit || canDelete) && (
                      <TableHead style={{ color: '#5B9BD5' }} className="px-4 py-3 text-xs text-right">Actions</TableHead>
                    )}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {lawazimConfigs?.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-8 px-4 text-gray-500 text-sm">
                        {searchTerm ? 'No lawazim configurations match your search' : 'No lawazim configurations found'}
                      </TableCell>
                    </TableRow>
                  ) : (
                    lawazimConfigs?.map((config, i) => (
                      <TableRow key={config?.id || i} className="hover:bg-sky-50/30">
                        <TableCell className="px-4 py-3">
                          <span className="text-gray-900 text-sm font-mono font-medium">
                            {config.code}
                          </span>
                        </TableCell>
                        <TableCell className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-sky-100 to-blue-100 flex items-center justify-center flex-shrink-0">
                              <Package className="w-4 h-4 text-[#5B9BD5]" />
                            </div>
                            <span className="text-gray-900 text-sm font-medium">
                              {/* {config.lawazimName} */}
                              {config?.name || "N/A"}
                            </span>
                          </div>
                        </TableCell>
                        <TableCell className="px-4 py-3">
                          <Badge
                            variant="outline"
                            className={`${(() => {
                              const normalizedChargeType = String(config.chargeType ?? '').toLowerCase();
                              if (normalizedChargeType === '1' || normalizedChargeType === 'daily-rate' || normalizedChargeType === 'dailyrate') {
                                return 'bg-blue-100 text-blue-700 border-blue-200';
                              }
                              if (normalizedChargeType === '2' || normalizedChargeType === 'fixed-rate' || normalizedChargeType === 'fixedrate') {
                                return 'bg-purple-100 text-purple-700 border-purple-200';
                              }
                              return 'bg-gray-100 text-gray-700 border-gray-200';
                            })()} text-xs`}
                          >
                            {(() => {
                              const normalizedChargeType = String(config.chargeType ?? '').toLowerCase();
                              if (normalizedChargeType === '1' || normalizedChargeType === 'daily-rate' || normalizedChargeType === 'dailyrate') {
                                return 'Daily Rate';
                              }
                              if (normalizedChargeType === '2' || normalizedChargeType === 'fixed-rate' || normalizedChargeType === 'fixedrate') {
                                return 'Fixed Rate';
                              }
                              return 'Unknown';
                            })()}
                          </Badge>
                        </TableCell>
                        <TableCell className="px-4 py-3">
                          <span className="text-gray-700 text-sm">
                            {config.validFrom
                              ? config.validFrom.split("T")[0]
                              : "-"}
                          </span>
                        </TableCell>
                        <TableCell className="px-4 py-3">
                          <span className="text-gray-700 text-sm">
                            {config.validTo
                              ? config.validTo.split("T")[0]
                              : "-"}
                          </span>
                        </TableCell>
                        <TableCell className="px-4 py-3">
                          <div className="flex items-center justify-center">
                            <Switch
                              checked={config.isActive}
                              onCheckedChange={() => toggleStatus(config)}
                              className="data-[state=checked]:bg-[#5B9BD5]"
                            />
                          </div>
                        </TableCell>
                        <TableCell className="px-4 py-3 text-right">
                          <div className="flex justify-end gap-2">
                            {canEdit && (
                              <Button
                                onClick={() => { navigate(`/lawazim-master/edit/${config.publicId}`) }}
                                size="sm"
                                variant="ghost"
                                className="text-[#5B9BD5] hover:text-[#4A8BC2] hover:bg-sky-50 h-8 w-8 p-0"
                              >
                                <Edit2 className="w-4 h-4" />
                              </Button>
                            )}
                            {canDelete && (
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleDeleteAtraaf(String(config.publicId))}
                                className="text-red-500 hover:bg-red-50 h-7 w-7 p-0"
                                title="Delete"
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
            {/* PAGINATION COMPONENT FOR DRAFT */}
            {LawazimTotalCount > 0 && <Pagination
              totalItems={LawazimTotalCount}
              itemsPerPage={filters.pageSize}
              currentPage={filters.pageNo}
              onPageChange={(page) =>
                setFilters((prev) => ({
                  ...prev,
                  pageNo: page, // convert back to 0-based
                }))
              }
              onPageSizeChange={(size) =>
                setFilters((prev) => ({
                  ...prev,
                  pageSize: size,
                  pageNo: 1, // IMPORTANT: reset page
                }))
              }
              label=""
            />}
          </CardContent>
        </Card>

        {/* STATUS CHANGE CONFIRMATION POPUP */}
        <ConfirmDialog
          show={false}
          open={isStatusDialogOpen}
          onOpenChange={setIsStatusDialogOpen}
          title="Confirm Status Change"
          description={`Are you sure you want to ${selectedItem?.isActive ? "deactivate" : "activate"
            } this place?`}
          confirmText="Yes, Confirm"
          onConfirm={confirmStatusChange}
          onCancel={cancelStatusChange}
        />

        {/* DELETE CONFIRMATION POPUP */}
        <ConfirmDialog
          open={deleteDialogOpen}
          onOpenChange={setDeleteDialogOpen}
          title="Confirm Delete"
          description="Are you sure you want to delete?"
          confirmText="Yes, Delete"
          // loading={isDeleting}
          onConfirm={confirmDeleteLawazim}
          onCancel={cancelDeleteDraft} // ✅ NOW IT WORKS
        >
        </ConfirmDialog>
      </div>
    </div>
  );
}